import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  type,
  withHooks,
  watchState,
} from '@ngrx/signals';
import { withDevtools } from '@ngrx-toolkit/core';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { Recorder } from 'src/api/models/recorder';
import { ApiService } from 'src/api';
import { inject } from '@angular/core/primitives/di';
import { computed } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ErrorService } from '../services/error.service';
import { ErrorTypes } from '../models/error-types.model';
import { HttpErrorResponse } from '@angular/common/http';
import { withRequestStatus } from './features/with-request-status.feature';

const COLLECTION = 'recorders';

export const RecordersStore = signalStore(
  withDevtools('recorders'),
  withRequestStatus(),
  withEntities({ entity: type<Recorder>(), collection: COLLECTION }),
  withComputed(store => ({
    recorders: computed(() => {
      if (store.loaded()) {
        return store.recordersEntities();
      }
      return [];
    }),
  })),
  withComputed(store => ({
    hasAnyRecorder: computed(() => store.recorders().length > 0),
  })),
  withMethods(store => {
    const apiClient = inject(ApiService);
    const errorService = inject(ErrorService);

    return {
      async getRecorders() {
        try {
          const recorders = await firstValueFrom(apiClient.recordersGet({}));

          const sortedRecorders = recorders.sort((a, b) =>
            (a?.name ?? '').localeCompare(b?.name ?? ''),
          );

          patchState(
            store,
            setAllEntities(sortedRecorders, { collection: COLLECTION }),
          );
          store.setLoaded();
        } catch (error) {
          const httpError = error as HttpErrorResponse;
          store.setErrors([]);
          errorService.handleError(
            String(httpError.status) === ErrorTypes.NOT_FOUND
              ? ErrorTypes.NOT_FOUND
              : ErrorTypes.DEFAULT,
          );
        }
      },
    };
  }),
  withMethods(store => ({
    refreshByIds(ids: string[]) {
      const currentRecorders = store.recordersEntities();
      const recordersToRefresh = currentRecorders.filter(recorder =>
        ids.includes(recorder.id),
      );

      if (recordersToRefresh.length > 0) {
        store.getRecorders();
      }
    },
    hasRecorderById(id: string) {
      return store.recorders().some(recorder => recorder.id === id);
    },
  })),
  withHooks({
    onInit: store => {
      const savedState = sessionStorage.getItem('recordersState');
      if (savedState) {
        patchState(store, JSON.parse(savedState));
      }

      watchState(store, state => {
        sessionStorage.setItem('recordersState', JSON.stringify(state));
      });

      if (!store.loaded()) {
        store.setLoading();
      }
    },
  }),
);
